import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function CompaniesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", currency: "USD", address: "", gstin: "", pan: "", tan: "", statutory_regime: "none", admin_email: "", admin_name: "", admin_password: "" });
  const [newCreds, setNewCreds] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Strip empty strings so backend Optional[EmailStr] accepts blanks
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      const { data } = await api.post("/companies", payload);
      toast.success("Client company created");
      setNewCreds(data.admin_credentials);
      setOpen(false);
      setForm({ name: "", currency: "USD", address: "", admin_email: "", admin_name: "", admin_password: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to create company"));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this client and all its data?")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to delete"));
    }
  };

  const copy = (s) => { navigator.clipboard.writeText(s); toast.success("Copied"); };

  return (
    <div>
      <PageHeader
        testid="page-companies"
        eyebrow="Client roster"
        title="Companies."
        description="Onboard the client companies your firm serves. Each gets an isolated workspace with an HR admin account."
        actions={
          <button onClick={() => setOpen(true)} data-testid="btn-add-company"
            className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2.5 font-mono text-sm hover:bg-neutral-950 transition-colors">
            <Plus className="w-4 h-4" /> New client
          </button>
        }
      />

      {newCreds && (
        <div className="mx-8 mt-6 border border-neutral-950 bg-neutral-950 text-white p-6" data-testid="new-creds-banner">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] mb-2">Save these credentials — shown once</div>
          <div className="flex flex-wrap gap-6 items-center">
            <div>
              <div className="text-xs font-mono text-neutral-400">Admin email</div>
              <div className="font-mono">{newCreds.email}</div>
            </div>
            <div>
              <div className="text-xs font-mono text-neutral-400">Password</div>
              <div className="font-mono">{newCreds.password}</div>
            </div>
            <button onClick={() => copy(`${newCreds.email} / ${newCreds.password}`)} className="ml-auto flex items-center gap-2 px-3 py-2 border border-white/30 hover:bg-white hover:text-neutral-950 font-mono text-xs transition-colors">
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
            <button onClick={() => setNewCreds(null)} className="px-3 py-2 border border-white/30 hover:bg-white hover:text-neutral-950 font-mono text-xs transition-colors">Dismiss</button>
          </div>
        </div>
      )}

      <div className="p-8">
        <div className="border border-neutral-200 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="companies-table">
            <thead className="bg-[#FAFAFA] border-b border-neutral-200">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Currency</th>
                <th className="px-4 py-3">Employees</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="p-6 text-neutral-500 font-mono">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-neutral-500 font-mono">No client companies yet. Click <strong>New client</strong> to get started.</td></tr>
              )}
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-neutral-100 hover:bg-[#FAFAFA] transition-colors" data-testid={`row-company-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-xs text-neutral-500 font-mono">{c.address || "—"}</div>
                    {c.gstin && <div className="text-[10px] font-mono text-neutral-500 mt-0.5">GSTIN {c.gstin}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono">{c.currency}</td>
                  <td className="px-4 py-3 font-mono">{c.employee_count}</td>
                  <td className="px-4 py-3 font-mono text-neutral-500">{c.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(c.id)} data-testid={`btn-delete-company-${c.id}`} className="inline-flex items-center gap-1 text-neutral-500 hover:text-[#FF3B30] font-mono text-xs transition-colors">
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-neutral-950/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-neutral-950 w-full max-w-xl" onClick={(e) => e.stopPropagation()} data-testid="dialog-new-company">
            <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#002FA7]">Onboard</div>
                <div className="font-display font-bold text-2xl tracking-tighter">New client company</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-neutral-500 hover:text-neutral-950 font-mono">✕</button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Company name *</label>
                <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="input-company-name"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Currency</label>
                <select value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} data-testid="input-currency"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm bg-white">
                  {["USD","INR","EUR","GBP","AED","CAD","AUD"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Address</label>
                <input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Statutory regime</label>
                <div className="grid grid-cols-2 gap-2">
                  <label className={`cursor-pointer border p-3 flex items-start gap-3 transition-colors ${form.statutory_regime === "none" ? "border-neutral-950 bg-neutral-50" : "border-neutral-300 hover:border-neutral-500"}`}>
                    <input type="radio" name="regime" value="none" checked={form.statutory_regime === "none"} onChange={() => setForm({...form, statutory_regime: "none"})} data-testid="regime-none" />
                    <div>
                      <div className="font-mono text-sm font-semibold">Simple / generic</div>
                      <div className="text-xs text-neutral-500 mt-0.5">Flat tax % and fixed deductions</div>
                    </div>
                  </label>
                  <label className={`cursor-pointer border p-3 flex items-start gap-3 transition-colors ${form.statutory_regime === "india" ? "border-neutral-950 bg-neutral-50" : "border-neutral-300 hover:border-neutral-500"}`}>
                    <input type="radio" name="regime" value="india" checked={form.statutory_regime === "india"} onChange={() => setForm({...form, statutory_regime: "india", currency: "INR"})} data-testid="regime-india" />
                    <div>
                      <div className="font-mono text-sm font-semibold">India · statutory</div>
                      <div className="text-xs text-neutral-500 mt-0.5">Auto EPF, ESI, PT, LWF, TDS FY25-26</div>
                    </div>
                  </label>
                </div>
              </div>
              <div className="col-span-2 border-t border-neutral-200 pt-4 mt-2 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                Tax identifiers (India · optional)
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">GSTIN (15 chars)</label>
                <input value={form.gstin} onChange={(e) => setForm({...form, gstin: e.target.value.toUpperCase()})}
                  data-testid="input-gstin" placeholder="e.g. 06ATUPP9372R1Z9" maxLength={15}
                  className={`w-full px-3 py-2.5 border ${form.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/.test(form.gstin) ? "border-[#FF3B30]" : "border-neutral-300"} focus:border-[#002FA7] outline-none font-mono text-sm uppercase`} />
                {form.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/.test(form.gstin) && (
                  <div className="mt-1 text-xs text-[#FF3B30] font-mono">GSTIN format looks off — 15 chars, e.g. 06ATUPP9372R1Z9</div>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Business PAN</label>
                <input value={form.pan} onChange={(e) => setForm({...form, pan: e.target.value.toUpperCase()})}
                  data-testid="input-pan" placeholder="ABCDE1234F" maxLength={10}
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm uppercase" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">TAN (for TDS)</label>
                <input value={form.tan} onChange={(e) => setForm({...form, tan: e.target.value.toUpperCase()})}
                  data-testid="input-tan" placeholder="ABCD12345E" maxLength={10}
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm uppercase" />
              </div>
              <div className="col-span-2 border-t border-neutral-200 pt-4 mt-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">HR admin (leave blank for auto)</div>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Admin name</label>
                <input value={form.admin_name} onChange={(e) => setForm({...form, admin_name: e.target.value})}
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Admin email</label>
                <input type="email" value={form.admin_email} onChange={(e) => setForm({...form, admin_email: e.target.value})}
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Admin password (default: Welcome@2026)</label>
                <input type="text" value={form.admin_password} onChange={(e) => setForm({...form, admin_password: e.target.value})}
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div className="col-span-2 flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2.5 font-mono text-sm hover:bg-neutral-100 transition-colors">Cancel</button>
                <button type="submit" data-testid="btn-submit-company" className="px-5 py-2.5 bg-[#002FA7] text-white font-mono text-sm hover:bg-neutral-950 transition-colors">Create workspace</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
